import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { api } from '~/api/client'
import type { ActNotice, AnyNotice, Notice, VenuesWeekNotice } from '~/api/types'
import { AppLink } from '~/components/AppLink'
import { waitingChanged } from './waiting'

import s from './Notices.module.css'

/**
 * The bell, and what is behind it.
 *
 * **Opened on a press, never on hover.** The account menu beside this one opens on hover
 * and that is right for it — passing a pointer over a menu costs nothing. Passing a pointer
 * over this one would mark everything read, so a reader who brushed the bar on the way to
 * the search box would lose the badge for notices they never saw. Hover is not a decision
 * and this control needs one.
 *
 * **Fetched first, stamped second**, and the order is the whole trick. The list arrives
 * carrying the `unread` flags as they were before the press, so the reader sees which ones
 * are new; the stamp then lands and the badge clears. Reversing the two would show a panel
 * where nothing is marked, which is a panel that cannot answer the question it was opened
 * to answer.
 *
 * Read is not gone. Everything stays in the list once it has been seen — this is a panel,
 * not an alert queue, and a notice that deletes itself the moment you glance at it takes
 * with it the only record that the thing happened.
 */
export function Notices({ unread }: { unread: number }) {
  const [open, setOpen] = useState(false)
  const [notices, setNotices] = useState<AnyNotice[] | null>(null)
  const box = useRef<HTMLDivElement>(null)

  const show = useCallback(() => {
    setOpen(true)
    void api
      .notices()
      .then(answer => {
        setNotices(answer.notices)
        // Only once there is something on screen to have been read. A stamp sent before
        // the list arrived would clear the badge for a request that then failed. With the
        // list's own `asOf`, so a gig that lands between the two stays unread.
        return api.noticed(answer.asOf)
      })
      // And tell the bar to re-count, which is what takes the badge off. `waitingChanged`
      // rather than a local number: the bell is drawn from `/waiting` like the other three
      // badges, so a private count here would be a second answer to one question.
      .then(waitingChanged)
      // A panel is not worth a visible failure, same as the badge that opens it.
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  return (
    <div className={s.wrap} ref={box}>
      <button
        type="button"
        className={s.bell}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread ? `Notices, ${unread} unread` : 'Notices'}
        onClick={() => (open ? setOpen(false) : show())}
      >
        <Bell ringing={unread > 0} />
        {unread > 0 ? <span className={s.badge}>{unread}</span> : null}
      </button>

      {open ? (
        <div className={s.panel} role="dialog" aria-label="Notices">
          {notices === null ? (
            <p className={s.quiet}>Looking…</p>
          ) : notices.length === 0 ? (
            <p className={s.quiet}>Nothing yet.</p>
          ) : (
            <ul className={s.list}>
              {notices.map(notice => (
                <Told key={keyOf(notice)} notice={notice} onGo={() => setOpen(false)} />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}

/**
 * What tells two rows apart. The fields that name what a row is about differ by kind, and a
 * key built from a field one kind lacks reads `undefined` for every row of that kind.
 */
function keyOf(notice: AnyNotice): string {
  switch (notice.kind) {
    case 'act-announced':
      return `${notice.kind}:${notice.eventId}`
    case 'venues-week':
      return `${notice.kind}:${notice.week}`
    default:
      return `${notice.kind}:${notice.accountId}`
  }
}

/**
 * One notice, in a sentence, and the whole row is the link: there is exactly one thing you
 * would want after reading it. A row with a separate "view" control would be a second target
 * for the same intent.
 *
 * Every kind shares the row, the unread rail and the time, so the panel reads as one list
 * whatever it holds; only the sentence and where it goes differ.
 */
function Told({ notice, onGo }: { notice: AnyNotice; onGo(): void }) {
  const [to, said] =
    notice.kind === 'act-announced'
      ? announced(notice)
      : notice.kind === 'venues-week'
        ? roundup(notice)
        : befriended(notice)
  return (
    <li className={notice.unread ? `${s.row} ${s.fresh}` : s.row}>
      <AppLink className={s.to ?? ''} to={to} onClick={onGo}>
        {said}
        <time className={s.when} dateTime={notice.at}>
          {ago(notice.at)}
        </time>
      </AppLink>
    </li>
  )
}

/**
 * Both friend kinds point at the person's page, which is where the thing you would do next
 * is: for a request, the buttons that answer it. A notice that says somebody asked and then
 * leaves you to find them is a notification in the sense of a noise.
 */
function befriended(notice: Notice): [string, ReactNode] {
  return [
    `/u/${notice.username}`,
    <span className={s.said}>
      <b>{notice.name}</b>{' '}
      {notice.kind === 'friend-asked' ? 'wants to be friends' : 'accepted your friend request'}
    </span>,
  ]
}

/**
 * A new gig by an act you follow. Led by the act's name rather than the gig's title because
 * the sentence is about the act you follow.
 *
 * One night opens the gig. A run opens the act, where every night of it is listed: the
 * notice's own gig is only the first of them, and opening it would hide the rest.
 */
function announced(notice: ActNotice): [string, ReactNode] {
  const room = <span className={s.room}>{notice.venue}</span>
  return notice.nights === 1
    ? [
        `/e/${notice.eventId}`,
        <span className={s.said}>
          <b>{notice.act}</b> at {room} · {notice.when}
        </span>,
      ]
    : [
        `/a/${notice.artistId}`,
        <span className={s.said}>
          <b>{notice.act}</b> announced {notice.nights} nights at {room} · {notice.when}
        </span>,
      ]
}

/**
 * The rooms you follow, a week at a time. It opens the My venues tab, `?view=venues` as
 * `readAddress` reads it, which is every one of those nights in one list.
 *
 * The rooms go on a line of their own that is cut with an ellipsis rather than wrapped: the
 * sentence above is the notice, and the rooms are a glance at where.
 */
function roundup(notice: VenuesWeekNotice): [string, ReactNode] {
  const added = `added ${notice.nights} ${notice.nights === 1 ? 'night' : 'nights'} this week`
  const rooms = notice.rooms.map(room => `${room.name} ${room.nights}`)
  if (notice.more > 0) rooms.push(`and ${notice.more} more ${notice.more === 1 ? 'room' : 'rooms'}`)
  return [
    '/calendar?view=venues',
    <span className={s.said}>
      <span>
        <b>Your venues</b> {added}
      </span>
      {rooms.length > 0 ? <span className={s.rooms}>{rooms.join(' · ')}</span> : null}
    </span>,
  ]
}

/**
 * How long ago, in as few characters as a row can spare.
 *
 * Coarse on purpose. "2h" and "3d" are the whole of what a notice needs to say about when —
 * the exact minute is in the `dateTime` for anything that wants it, and a panel of full
 * dates reads as a log rather than as a list of things that happened to you.
 */
export function ago(at: string, now: Date = new Date()): string {
  // The server sends fixed-width UTC with a `Z`, which `Date` parses as UTC. Without the
  // `Z` it would be read as local time and every notice would be an hour out in summer.
  const then = new Date(at).getTime()
  const mins = Math.floor((now.getTime() - then) / 60_000)
  if (!Number.isFinite(mins)) return ''
  // Clamped at nought rather than shown negative: a clock a few seconds behind the server's
  // would otherwise render "in 1m" for something that has already happened.
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  const weeks = Math.floor(days / 7)
  if (weeks < 53) return `${weeks}w`
  return `${Math.floor(days / 365)}y`
}

/**
 * Drawn rather than typed, for the reason `Bookmark` and `Star` give: `🔔` is an emoji whose
 * shape, colour and weight are the platform's to choose, and this one sits at 15px beside
 * two paths that are neither.
 */
function Bell({ ringing }: { ringing: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* The body, and the clapper below it. Squared shoulders, like the bookmark. */}
      <path d="M18 8.5a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16s-2-1.5-2-6.5Z" />
      <path d="M13.7 18.5a2 2 0 0 1-3.4 0" />
      {/* Two strokes at the mouth when something is waiting. Not a colour change: the badge
          beside it already carries the colour, and a bell that only differs by hue says
          nothing to a reader who cannot see the difference. */}
      {ringing ? <path d="M3.2 5.6 1.6 4.2M20.8 5.6l1.6-1.4" /> : null}
    </svg>
  )
}
